import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../StyledTypography';
import Checkbox from 'apollo-react/components/Checkbox';
import Table from 'apollo-react/components/Table';
import IconButton from 'apollo-react/components/IconButton';
import CircularProgress from 'apollo-react/components/CircularProgress';
import OpenNew from 'apollo-react-icons/OpenNew';
import styles from '../LinkedStudies/LinkedStudies.module.css';

const toId = (value) => Number(value);

/**
 * Multi-select list of eligible Approved Parent Requests for sub-workflow creation.
 */
const ParentRequestSelector = ({
  eligibleParents = [],
  selectedParentRequestIds = [],
  onSelectionChange,
  loading = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const selectedIds = useMemo(
    () =>
      selectedParentRequestIds.map(toId).filter((id) => Number.isInteger(id)),
    [selectedParentRequestIds]
  );

  const handleToggle = useCallback(
    (requestId) => {
      const id = toId(requestId);
      const next = selectedIds.includes(id)
        ? selectedIds.filter((selected) => selected !== id)
        : [...selectedIds, id];
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange]
  );

  const handleSelectAll = useCallback(() => {
    const visibleIds = eligibleParents.map((row) => toId(row.id));
    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      onSelectionChange(selectedIds.filter((id) => !visibleIds.includes(id)));
      return;
    }
    onSelectionChange([...new Set([...selectedIds, ...visibleIds])]);
  }, [eligibleParents, selectedIds, onSelectionChange]);

  const columns = useMemo(
    () => [
      {
        header: (
          <Checkbox
            checked={
              eligibleParents.length > 0 &&
              eligibleParents.every((row) => selectedIds.includes(toId(row.id)))
            }
            indeterminate={
              eligibleParents.some((row) =>
                selectedIds.includes(toId(row.id))
              ) &&
              !eligibleParents.every((row) =>
                selectedIds.includes(toId(row.id))
              )
            }
            onChange={handleSelectAll}
          />
        ),
        accessor: 'select',
        customCell: ({ row }) => (
          <Checkbox
            checked={selectedIds.includes(toId(row.id))}
            onChange={() => handleToggle(row.id)}
            inputProps={{ 'aria-label': `Select parent request ${row.id}` }}
          />
        ),
        width: 60,
      },
      {
        header: t('Request Name', 'Request Name'),
        accessor: 'study_name',
        customCell: ({ row }) => row.study_name || row.generated_id,
      },
      {
        header: t('ID'),
        accessor: 'generated_id',
      },
      {
        header: t('Workflow', 'Workflow'),
        accessor: 'workflow_name',
        customCell: ({ row }) => row.workflow_name || '-',
      },
      {
        header: t('Approval Date', 'Approval Date'),
        accessor: 'date_ended',
        customCell: ({ row }) =>
          row.date_ended ? new Date(row.date_ended).toLocaleDateString() : '-',
      },
      {
        accessor: 'actions',
        customCell: ({ row }) => {
          const requestName = row.study_name || row.generated_id;
          const handleViewDetails = (e) => {
            e.stopPropagation();
            const url = `/enduser/progress/requests/${row.id}?request_name=${encodeURIComponent(requestName)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          };
          return (
            <IconButton
              size="small"
              onClick={handleViewDetails}
              title={t('view_details')}
            >
              <OpenNew />
            </IconButton>
          );
        },
        width: 80,
      },
    ],
    [t, selectedIds, eligibleParents, handleSelectAll, handleToggle]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography variant="bodyDefault">
          {t(
            'parentRequest.selectPrompt',
            'Select one or more Approved Study Notifications to link this submission.'
          )}
        </Typography>
      </div>

      {selectedIds.length > 0 && (
        <div className={styles.selectionSummary}>
          <Typography variant="bodySmall" className={styles.selectionCount}>
            {selectedIds.length} {t('selected')}
          </Typography>
        </div>
      )}

      {eligibleParents.length === 0 ? (
        <div className={styles.emptyContainer}>
          <Typography variant="bodyDefault">
            {t(
              'parentRequest.noEligible',
              'No approved study notifications are available for this workflow.'
            )}
          </Typography>
        </div>
      ) : (
        <Table
          columns={columns}
          rows={eligibleParents}
          rowId="id"
          hidePagination={eligibleParents.length <= 10}
        />
      )}
    </div>
  );
};

export default ParentRequestSelector;
